/**
 * Signing staff in and out of a device that is already logged in to a property.
 *
 * Two tiers, deliberately:
 *
 *   1. The **property session** is established once, by a manager, with an email
 *      and password (`POST /User/login`). It lasts for the life of the install.
 *   2. A **staff session** sits inside it. Waiters sign in with a 4-digit PIN and
 *      hand the device back many times a service, so it has to be quick and it
 *      must never cost the property session.
 *
 * `lock()` therefore ends only the staff session. Only `logout()` in AuthContext
 * ends the property session, and that is a manager-level act.
 *
 * ── API contract ──────────────────────────────────────────────────────────────
 * `authenticate-pin` does not exist on the server yet. The mock implements the
 * shape below (see mockApi.ts) so the flow is real end to end; the backend needs
 * to match it. Three things matter beyond the endpoint itself:
 *
 *   • **PINs must be unique per property.** Signing in by PIN with nobody to pick
 *     from a list is only possible if a PIN identifies exactly one person. Today
 *     nothing enforces this, in the admin UI or the API, so two people can hold
 *     1234. Enforce it on write and reject the collision there, not here.
 *   • **Rate limiting is not optional.** Four digits is 10,000 possibilities; a
 *     tablet left on a bar can be walked through them. The client throttles
 *     (see PIN_LOCKOUT below) but that is a courtesy, not a defence.
 *   • **PINs should not be stored in plain text**, and should never come back on
 *     a read. They are plaintext on the user record today.
 */
import { User } from '../interfaces';

const API = process.env.REACT_APP_API_URL;

/** Who is signed in, as far as the PIN screen and the app chrome care. */
export interface StaffSession {
    id: string;
    userName: string;
    firstName: string;
    lastName: string;
    displayName: string;
    roleName: string;
    profilePicture?: string;
}

export type PinResult =
    | { ok: true; staff: StaffSession }
    | { ok: false; reason: 'unknown' | 'inactive' | 'ambiguous' | 'throttled' | 'offline'; message: string };

export const PIN_LENGTH = 4;

/** After this many wrong PINs the pad stops accepting input for a moment. */
export const PIN_LOCKOUT = { attempts: 5, cooldownMs: 30_000 };

/**
 * Exchange a PIN for a staff session. The property is taken from the device
 * rather than from the caller, so a PIN typed on this tablet can only ever match
 * someone who works here.
 */
export async function signInWithPin(pin: string): Promise<PinResult> {
    const propertyId = localStorage.getItem('propertyId') || '';
    try {
        const res = await fetch(`${API}/Authentication/authenticate-pin`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ propertyId, pin }),
        });

        if (res.ok) {
            const staff = (await res.json()) as StaffSession;
            return { ok: true, staff };
        }

        switch (res.status) {
            case 403:
                return { ok: false, reason: 'inactive', message: 'That account is no longer active' };
            case 409:
                // Two people share this PIN, so it identifies nobody. Says what to
                // do about it, because the person holding the tablet cannot fix it.
                return { ok: false, reason: 'ambiguous', message: 'That PIN is shared by two people — ask a manager to change one' };
            case 429:
                return { ok: false, reason: 'throttled', message: 'Too many attempts. Try again in a moment' };
            default:
                return { ok: false, reason: 'unknown', message: 'PIN not recognised' };
        }
    } catch {
        return { ok: false, reason: 'offline', message: 'No connection — cannot sign in right now' };
    }
}

/**
 * Confirm the venue's own email and password without changing any session.
 *
 * Used to gate signing the device out of the venue: that ends the session for
 * everyone, so a 4-digit PIN must not be enough to do it. Anyone can reach the
 * button on the lock screen, and this is what stops them.
 */
export async function verifyPropertyCredentials(
    email: string,
    password: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
    try {
        const res = await fetch(`${API}/User/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ username: email, password }),
        });

        if (!res.ok) return { ok: false, message: 'Those details were not accepted' };

        // Valid credentials for a *different* venue must not sign this device out.
        const propertyId = await res.json();
        const current = localStorage.getItem('propertyId');
        if (current && typeof propertyId === 'string' && propertyId && propertyId !== current) {
            return { ok: false, message: 'Those details belong to a different venue' };
        }
        return { ok: true };
    } catch {
        return { ok: false, message: 'No connection — cannot check those details' };
    }
}

/** Someone with the authority to approve, and who they are for the record. */
export interface PinAuthority {
    id: string;
    displayName: string;
    roleName: string;
}

export type AuthorityResult =
    | { ok: true; approver: PinAuthority }
    | { ok: false; reason: 'unknown' | 'unauthorised' | 'offline'; message: string };

/**
 * Check that a PIN belongs to somebody allowed to do `requiredPermission`, without
 * touching the current session.
 *
 * This is deliberately not `signInWithPin`: an approval is a second person
 * leaning over and authorising something, and the waiter must still be signed in
 * afterwards, still owning the shift they are closing.
 */
export async function verifyPinAuthority(
    pin: string,
    requiredPermission: string,
): Promise<AuthorityResult> {
    const propertyId = localStorage.getItem('propertyId') || '';
    try {
        const res = await fetch(`${API}/Authentication/verify-pin`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ propertyId, pin, requiredPermission }),
        });

        if (res.ok) {
            const approver = (await res.json()) as PinAuthority;
            return { ok: true, approver };
        }
        if (res.status === 403) {
            return {
                ok: false,
                reason: 'unauthorised',
                message: 'That PIN is not allowed to approve this',
            };
        }
        return { ok: false, reason: 'unknown', message: 'PIN not recognised' };
    } catch {
        return { ok: false, reason: 'offline', message: 'No connection — cannot check that PIN' };
    }
}

/** End the staff session, keeping the property logged in. */
export async function lockDevice(): Promise<void> {
    try {
        await fetch(`${API}/Authentication/lock`, { method: 'POST', credentials: 'include' });
    } catch {
        // Locking must never be blocked by the network: the point is to stop the
        // next person acting as this one, and that is decided on the client.
    }
}

/** The signed-in staff member's full record, for screens that need more than a name. */
export async function fetchActiveStaff(): Promise<User | null> {
    try {
        const res = await fetch(`${API}/User/get-by-email`, { credentials: 'include' });
        if (!res.ok) return null;
        return (await res.json()) as User;
    } catch {
        return null;
    }
}
